import React, { useEffect, useMemo, useRef, useState } from "react"
import moment from "moment"
import DepDispatchPlanning, { lieuCourt } from "./DepDispatchPlanning"
import { useDispatch, useSelector } from "react-redux"
import {
	fetchDmPlanningEnSuspens,
	fetchDmPlanningRdv,
	corpsDelta,
	putDepannageDispatch,
	putDepannageDispatchReset,
} from "../../../store/actions/depannageDispatchActions"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSync } from "@fortawesome/free-solid-svg-icons"
import DepDispatchDispatch from "./DepDispatchDispatch"
import DepMissionV2 from "../depmission/DepMissionV2"
import { classerRdv } from "./rdvATraiter"
import { useMissionsModifiees } from "../../../utilities/notificationsSocket"
import {
	DEP_STATE_EN_SUSPENS,
	DEP_STATE_RENDEZ_VOUS,
} from "../../../utilities/Addons"
import { fetchInformations } from "../../../store/actions/informationActions"
import SignalementModal from "../../signalement/SignalementModal"
import { COLORS } from "../../../utilities/Constants"
import { DISPATCH_POLL_MS } from "./DepDispatchATraiter"

// Couleurs de l'état EN_SUSPENS (mêmes valeurs que DepMissionCardV2).
const SUSPENS_COLOR = "#7C2D12"
const SUSPENS_BG = "#FFEDD5"

// B2 : étiquettes du rail « RDV à traiter ».
const TONS_RDV = {
	urgent: { color: COLORS.danger, backgroundColor: COLORS.dangerLight },
	hors: { color: COLORS.warning, backgroundColor: COLORS.warningLight },
	tard: { color: COLORS.accent, backgroundColor: COLORS.accentLight },
}
const RDV_COLOR = "#6D28D9"
const RDV_BG = "#F5F3FF"

// Layout 3 colonnes du planning (tokens V2).
const stPl = {
	chipRdv: {
		padding: "8px 10px",
		borderRadius: 8,
		border: `1px solid ${COLORS.border}`,
		borderLeft: `3px solid ${RDV_COLOR}`,
		backgroundColor: RDV_BG,
		marginBottom: 8,
		cursor: "grab",
	},
	tagRdv: (ton) => ({
		display: "inline-block",
		marginTop: 4,
		padding: "1px 8px",
		borderRadius: 99,
		fontSize: 10.5,
		fontWeight: 700,
		...TONS_RDV[ton],
	}),
	countUrgent: {
		padding: "1px 8px",
		borderRadius: 99,
		fontSize: 11,
		fontWeight: 700,
		color: COLORS.danger,
		backgroundColor: COLORS.dangerLight,
	},
	plusTard: {
		display: "flex",
		alignItems: "center",
		gap: 6,
		width: "100%",
		padding: "4px 0",
		border: "none",
		background: "none",
		cursor: "pointer",
		fontSize: 12,
		fontWeight: 600,
		color: COLORS.textMuted,
		fontFamily: "inherit",
	},
	sepRail: { height: 14 },
	container: {
		display: "flex",
		gap: 16,
		alignItems: "flex-start",
		width: "100%",
	},
	railGauche: { flex: "0 0 230px", minWidth: 0 },
	centre: {
		flex: 1,
		minWidth: 0,
		display: "flex",
		flexDirection: "column",
		gap: 14,
	},
	railDroit: { flex: "0 0 250px", minWidth: 0 },
	railTitre: {
		display: "flex",
		alignItems: "center",
		gap: 8,
		fontSize: 11,
		fontWeight: 700,
		letterSpacing: "0.08em",
		textTransform: "uppercase",
		color: COLORS.textLight,
		margin: "4px 0 10px",
	},
	count: {
		padding: "1px 8px",
		borderRadius: 99,
		fontSize: 11,
		fontWeight: 700,
		color: COLORS.accent,
		backgroundColor: COLORS.accentLight,
	},
	chip: {
		padding: "8px 10px",
		borderRadius: 8,
		border: `1px solid ${COLORS.border}`,
		borderLeft: `3px solid ${SUSPENS_COLOR}`,
		backgroundColor: SUSPENS_BG,
		marginBottom: 8,
		cursor: "grab",
	},
	chipSpinner: {
		display: "flex",
		justifyContent: "center",
		padding: "6px 0",
		color: SUSPENS_COLOR,
	},
	chipTitre: { fontSize: 13, fontWeight: 700, color: COLORS.text },
	chipMeta: { fontSize: 11, color: COLORS.textMuted },
	vide: { fontSize: 13, color: COLORS.textLight, marginBottom: 10 },
	dropHint: {
		marginTop: 10,
		padding: "10px 12px",
		borderRadius: 8,
		border: `1px dashed ${COLORS.borderStrong}`,
		fontSize: 11,
		color: COLORS.textLight,
		lineHeight: 1.5,
	},
	loading: {
		display: "flex",
		justifyContent: "center",
		padding: "20px 0",
		color: COLORS.textLight,
	},
}

// Styles de la colonne signalements (tokens V2 dans une page encore legacy).
const stInfos = {
	empty: { fontSize: 13, color: COLORS.textLight },
	card: {
		padding: "10px 12px",
		borderRadius: 10,
		border: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.surface,
		marginBottom: 8,
		width: "100%",
		boxSizing: "border-box",
		cursor: "pointer",
	},
	meta: { fontSize: 11, color: COLORS.textLight, marginBottom: 4 },
	texte: { fontSize: 13, color: COLORS.text, whiteSpace: "pre-line" },
	photos: { fontSize: 11, color: COLORS.accent, marginTop: 4 },
}

const DepDispatchPlanningMenu = () => {
	const [openDepDispatchModalInfos, setOpenDepDispatchModalInfos] = useState({
		open: false,
		dm: null,
	})
	// Signalement ouvert en modal (null = fermé).
	const [signalementOuvert, setSignalementOuvert] = useState(null)
	const [resizeMission, setResizeMission] = useState({
		resizing: false,
		dm: null,
		startY: 0,
		delta: 0,
	})
	const [dmLoading, setDmLoading] = useState(0)
	// B2 : repliable « Plus tard » (fermé par défaut) + horloge à la minute
	// (un RDV qui atteint son heure passe en tête du rail).
	const [plusTardOuvert, setPlusTardOuvert] = useState(false)
	const [maintenant, setMaintenant] = useState(() => moment())
	useEffect(() => {
		const t = setInterval(() => setMaintenant(moment()), 60 * 1000)
		return () => clearInterval(t)
	}, [])
	// B3 : « Modifier la mission » depuis le modal Traiter.
	const [missionEditee, setMissionEditee] = useState(null)
	const resizeMissionRef = useRef({
		resizing: false,
		dm: null,
		startY: 0,
		delta: 0,
	})
	const [nbJoursAffiches, setNbJoursAffiches] = useState(0)
	const nbJoursAffichesRef = useRef(2)
	const dispatch = useDispatch()
	const {
		depsEnSuspens,
		depsRendezVous,
		fetchDepsEnSuspens,
		fetchDepsRendezVous,
		putDepsToDispatch,
	} = useSelector((state) => state.depannageDispatchInfo)
	const { informations } = useSelector((state) => state.informationsInfo)
	const loadingDepsRendezVousRef = useRef(false)
	const openDepDispatchModal = (dm) => {
		setOpenDepDispatchModalInfos({ open: true, dm })
	}
	const closeDepDispatchModal = () => {
		// Revue 5 (🟡) : un 409 du modal Traiter → le planning relit (la
		// copie affichée est périmée).
		if (
			putDepsToDispatch.error &&
			putDepsToDispatch.error.httpStatus === 409
		) {
			refreshRdvs()
			refreshEnSuspens()
		}
		setOpenDepDispatchModalInfos({ open: false, dm: null })
		dispatch(putDepannageDispatchReset())
	}
	// Fin d'un PUT initié par le planning (drop / resize) : reset du slice
	// partagé (sinon le modal Traiter croit ensuite que SA sauvegarde vient de
	// réussir → boutons busy + fermeture auto) + refresh explicite.
	// Revue phase B (🟡 D) : le bloc reste « en cours » jusqu'au retour des
	// listes — un redimensionnement immédiat part de la NOUVELLE version.
	// Revue 5 (🟡) : ne libère que SON bloc (un autre drop a pu suivre).
	const apresPutPlanning = (dmId) => {
		dispatch(putDepannageDispatchReset())
		Promise.all([refreshRdvs(), refreshEnSuspens()]).finally(() =>
			setDmLoading((id) => (id === dmId ? 0 : id)),
		)
	}
	const isBottom = () =>
		window.innerHeight + window.scrollY >=
		document.documentElement.offsetHeight
	const handleScroll = () => {
		if (isBottom() && nbJoursAffichesRef.current < 7) {
			nbJoursAffichesRef.current += 1
			setNbJoursAffiches(nbJoursAffichesRef.current)
		}
	}
	const refreshRdvs = () => dispatch(fetchDmPlanningRdv())
	const refreshEnSuspens = () => dispatch(fetchDmPlanningEnSuspens())
	// GE-62 (revue phase B, 🟠5) : le planning suit les missions écrites
	// ailleurs (et ne repart plus d'une copie périmée au drop suivant).
	// Revue 5 (🟡) : seulement en suspens (23) / rendez-vous (27), ou un
	// bloc affiché qui en sort.
	useMissionsModifiees(
		() => {
			refreshRdvs()
			refreshEnSuspens()
		},
		{
			filtre: (d) =>
				d.etat === 23 ||
				d.etat === 27 ||
				allDms.some((m) => m.id === d.id),
		},
	)
	const onDragStartEnSuspens = (e, dmId) => {
		e.stopPropagation()
		e.dataTransfer.setData("dmId", dmId.toString())
	}
	const onDragOverEnSuspens = (e) => {
		e.preventDefault()
	}
	const onDropEnSuspens = (e) => {
		e.preventDefault()
		let dmId = parseInt(e.dataTransfer.getData("dmId"))
		const dm = allDms.find((dm) => dm.id === dmId)
		// Revue phase B (🟡 L) : mission sortie des listes entre-temps.
		if (!dm) return
		setDmLoading(dm.id)
		// GE-62 (B6) : delta + base_version (409 si la mission a bougé).
		dispatch(
			putDepannageDispatch(
				corpsDelta(dm, {
					etat: DEP_STATE_EN_SUSPENS,
					date_rendez_vous: null,
					delta_rendez_vous: 0,
				}),
				{ silent: false },
			),
		).then(() => apresPutPlanning(dm.id))
	}
	const onDropPlanning = (e, date, time) => {
		e.preventDefault()
		let dmId = parseInt(e.dataTransfer.getData("dmId"))
		const dm = allDms.find((dm) => dm.id === dmId)
		// Revue phase B (🟡 L) : mission sortie des listes entre-temps.
		if (!dm) return
		setDmLoading(dm.id)
		const changes = {
			etat: DEP_STATE_RENDEZ_VOUS,
			date_rendez_vous: `${date} ${time}`,
		}
		if (dm.delta_rendez_vous === 0) changes.delta_rendez_vous = 90
		dispatch(
			putDepannageDispatch(corpsDelta(dm, changes), { silent: false }),
		).then(() => apresPutPlanning(dm.id))
	}
	const allDms = useMemo(() => {
		return [...depsEnSuspens, ...depsRendezVous]
	}, [depsEnSuspens, depsRendezVous])
	const formattedRdv = useMemo(() => {
		const showDates = []
		for (let i = 0; i < nbJoursAffiches; i++) {
			showDates.push(moment().add(i, "days").format("DD-MM-YYYY"))
		}
		let objsByDate = {}
		showDates.forEach((d) => (objsByDate[d] = []))
		depsRendezVous
			.filter((dm) =>
				showDates.includes(dm.date_rendez_vous.split(" ")[0]),
			)
			.forEach((dm) => {
				let d = dm.date_rendez_vous.split(" ")[0]
				if (!objsByDate[d]) objsByDate[d] = []
				objsByDate[d].push(dm)
			})
		let rdvs = []
		for (let [k, v] of Object.entries(objsByDate)) {
			rdvs.push({
				date: k,
				missions: v,
			})
		}
		return rdvs
	}, [depsRendezVous, nbJoursAffiches])
	const mouseMove = (e) => {
		if (!resizeMissionRef.current.resizing || !resizeMissionRef.current.dm)
			return
		let delta = e.clientY - resizeMissionRef.current.startY
		let delta30 = Math.ceil(delta / 40)
		if (
			resizeMissionRef.current.dm.delta_rendez_vous + delta30 * 30 <=
			60
		) {
			delta =
				-(resizeMissionRef.current.dm.delta_rendez_vous - 60 / 30) * 40
		}
		// if (delta < 40) delta = 40
		resizeMissionRef.current = {
			...resizeMissionRef.current,
			delta,
		}
		setResizeMission(resizeMissionRef.current)
	}
	const mouseUp = () => {
		if (!resizeMissionRef.current.resizing || !resizeMissionRef.current.dm)
			return
		let delta30 = Math.ceil(resizeMissionRef.current.delta / 40)
		if (delta30 !== 0) {
			const dm = resizeMissionRef.current.dm
			setDmLoading(dm.id)
			const delta = Math.max(60, dm.delta_rendez_vous + delta30 * 30)
			dispatch(
				putDepannageDispatch(
					corpsDelta(dm, { delta_rendez_vous: delta }),
					{ silent: false },
				),
			).then(() => apresPutPlanning(dm.id))
		}
		resizeMissionRef.current = {
			resizing: false,
			dm: null,
			startY: 0,
			delta: 0,
		}
		setResizeMission(resizeMissionRef.current)
	}
	useEffect(() => {
		setNbJoursAffiches(nbJoursAffichesRef.current)
	}, [nbJoursAffichesRef.current])
	useEffect(() => {
		if (putDepsToDispatch.saved) {
			refreshRdvs()
			refreshEnSuspens()
		}
	}, [putDepsToDispatch.saved])
	useEffect(() => {
		loadingDepsRendezVousRef.current = fetchDepsRendezVous.loading
	}, [fetchDepsRendezVous.loading])
	useEffect(() => {
		window.addEventListener("scroll", handleScroll)
		window.addEventListener("mousemove", mouseMove)
		window.addEventListener("mouseup", mouseUp)
		refreshRdvs()
		refreshEnSuspens()
		// categorie=signalement : le rail ne veut QUE les signalements (pas les
		// autres catégories de notifications — stock, location…).
		dispatch(fetchInformations("actifs", { categorie: "signalement" }))
		// Polling : le planning est partagé entre opérateurs.
		const interval = setInterval(() => {
			refreshRdvs()
			refreshEnSuspens()
			dispatch(fetchInformations("actifs", { categorie: "signalement" }))
		}, DISPATCH_POLL_MS)
		return () => {
			window.removeEventListener("scroll", handleScroll)
			window.removeEventListener("mousemove", mouseMove)
			window.removeEventListener("mouseup", mouseUp)
			clearInterval(interval)
		}
	}, [])
	const rdvsATraiter = classerRdv(depsRendezVous, maintenant)
	const chipRdv = (dm, date, tag, ton) => (
		<div
			style={stPl.chipRdv}
			onClick={() => openDepDispatchModal(dm)}
			draggable={true}
			onDragStart={(e) => onDragStartEnSuspens(e, dm.id)}
		>
			{dmLoading === dm.id ? (
				<div style={stPl.chipSpinner}>
					<FontAwesomeIcon icon={faSync} spin />
				</div>
			) : (
				<>
					<div style={stPl.chipTitre}>
						{`${dm.marque} ${dm.modele}`.trim() || "—"}
					</div>
					<div style={stPl.chipMeta}>
						{[
							lieuCourt(dm.lieu_intervention, [
								dm.lieu_intervention_0,
								dm.lieu_intervention_1,
							]),
							date.format("DD.MM HH:mm"),
						]
							.filter(Boolean)
							.join(" · ")}
					</div>
					<span style={stPl.tagRdv(ton)}>{tag}</span>
				</>
			)}
		</div>
	)
	const boutonPlusTard = (
		<button
			type="button"
			style={stPl.plusTard}
			onClick={() => setPlusTardOuvert((o) => !o)}
		>
			{plusTardOuvert ? "▾" : "▸"} Plus tard (
			{rdvsATraiter.plusTard.length})
		</button>
	)

	return (
		<>
			{openDepDispatchModalInfos.open && (
				<DepDispatchDispatch
					visible={openDepDispatchModalInfos.open}
					closeModal={closeDepDispatchModal}
					dm={openDepDispatchModalInfos.dm}
					onlyDispatch={true}
					onModifier={(dm) => {
						closeDepDispatchModal()
						setMissionEditee(dm)
					}}
				/>
			)}
			{missionEditee && (
				<DepMissionV2
					visible
					editDepMission={missionEditee}
					closeModal={() => setMissionEditee(null)}
					refreshDeps={() => {
						refreshRdvs()
						refreshEnSuspens()
					}}
				/>
			)}
			<div style={stPl.container}>
				{/* Rail gauche : missions en suspens (source ET cible de drag). */}
				<div
					style={stPl.railGauche}
					onDragOver={onDragOverEnSuspens}
					onDrop={onDropEnSuspens}
				>
					{/* B2 (CDC 16.10) : RDV que la grille ne montre pas ou à
					    risque — heure passée (rouge, en tête), hors 07:00–18:00,
					    puis « Plus tard » (au-delà de 7 jours, replié). */}
					<div style={stPl.railTitre}>
						RDV à traiter
						<span style={stPl.countUrgent}>
							{rdvsATraiter.urgents.length +
								rdvsATraiter.horsPlage.length}
						</span>
					</div>
					{rdvsATraiter.urgents.length +
						rdvsATraiter.horsPlage.length ===
						0 && <div style={stPl.vide}>Aucun RDV à traiter</div>}
					{[
						...rdvsATraiter.urgents.map((x) => ({
							...x,
							ton: "urgent",
						})),
						...rdvsATraiter.horsPlage.map((x) => ({
							...x,
							ton: "hors",
						})),
						...(plusTardOuvert
							? rdvsATraiter.plusTard.map((x) => ({
									...x,
									ton: "tard",
								}))
							: []),
					].map(({ dm, date, tag, ton }, i, liste) => (
						<React.Fragment key={`rdv_${dm.id}`}>
							{ton === "tard" &&
								(i === 0 || liste[i - 1].ton !== "tard") &&
								boutonPlusTard}
							{chipRdv(dm, date, tag, ton)}
						</React.Fragment>
					))}
					{rdvsATraiter.plusTard.length > 0 &&
						!plusTardOuvert &&
						boutonPlusTard}
					<div style={stPl.sepRail} />
					<div style={stPl.railTitre}>
						En suspens
						<span style={stPl.count}>{depsEnSuspens.length}</span>
					</div>
					{depsEnSuspens.length === 0 ? (
						<div style={stPl.vide}>Aucune mission en suspens</div>
					) : (
						depsEnSuspens.map((dep) => (
							<div
								key={`en_suspens_${dep.id}`}
								style={stPl.chip}
								onClick={() => openDepDispatchModal(dep)}
								draggable={true}
								onDragStart={(e) =>
									onDragStartEnSuspens(e, dep.id)
								}
							>
								{dmLoading === dep.id ? (
									<div style={stPl.chipSpinner}>
										<FontAwesomeIcon icon={faSync} spin />
									</div>
								) : (
									<>
										<div style={stPl.chipTitre}>
											{`${dep.marque} ${dep.modele}`.trim() ||
												"—"}
										</div>
										<div style={stPl.chipMeta}>
											{/* A15 : localité (ou lieu nommé)
											    de l'intervention ; `localite`
											    vit dans `adresse`. */}
											{lieuCourt(dep.lieu_intervention, [
												dep.lieu_intervention_0,
												dep.lieu_intervention_1,
											])}
										</div>
									</>
								)}
							</div>
						))
					)}
					<div style={stPl.dropHint}>
						Glisser une mission ici pour la suspendre — ou la
						déposer sur un créneau de la grille pour la planifier.
					</div>
				</div>

				{/* Centre : grilles de jours (aujourd'hui puis suivants au
				    scroll, 7 max). */}
				<div style={stPl.centre}>
					{formattedRdv.map((d, index) => (
						<DepDispatchPlanning
							key={`planning_${index}`}
							data={d}
							index={index}
							addExtraHeight={
								formattedRdv.length === 1 &&
								!fetchDepsRendezVous.loading
							}
							onClickDm={openDepDispatchModal}
							onDropPlanning={onDropPlanning}
							resizeMissionRef={resizeMissionRef}
							resizeMission={resizeMission}
							dmLoading={dmLoading}
						/>
					))}
					{fetchDepsRendezVous.loading && (
						<div style={stPl.loading}>
							<FontAwesomeIcon icon={faSync} spin />
						</div>
					)}
				</div>

				{/* Rail droit : signalements actifs (remontées mobiles) — le
				    dispatcher les a sous les yeux en planifiant. */}
				<div style={stPl.railDroit}>
					<div style={stPl.railTitre}>
						Signalements
						<span style={stPl.count}>{informations.length}</span>
					</div>
					{informations.length === 0 ? (
						<p style={stInfos.empty}>Aucun signalement actif</p>
					) : (
						informations.map((info) => (
							<div
								key={`signalement_${info.id}`}
								style={stInfos.card}
								onClick={() => setSignalementOuvert(info)}
							>
								<div style={stInfos.meta}>
									{`${info.initiale} · ${info.created_at}`}
								</div>
								<div style={stInfos.texte}>{info.texte}</div>
								{info.photos && info.photos.length > 0 ? (
									<div style={stInfos.photos}>
										{`${info.photos.length} photo${
											info.photos.length > 1 ? "s" : ""
										}`}
									</div>
								) : null}
							</div>
						))
					)}
				</div>
			</div>
			{signalementOuvert && (
				<SignalementModal
					info={signalementOuvert}
					fetchOpts={{ categorie: "signalement" }}
					onClose={() => setSignalementOuvert(null)}
				/>
			)}
		</>
	)
}

export default DepDispatchPlanningMenu
